import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { useWorkspace } from '../workspace/WorkspaceContext'
import { api, API_URL } from '../../shared/api/http'
import type { Client, Company } from '../monitoring/monitoringApi'
import { WindowsDownloadLink } from '../../shared/WindowsDownloadLink'
import { StationDownloads } from '../../shared/StationDownloads'

type ProfileSummary = { pendingItems: string[] }
type ClientEntry = { company: Company; client: Client }

const pendingLabels: Record<string, string> = {
  email_confirmation: 'Confirmação do e-mail', phone_confirmation: 'Confirmação do telefone', cpf: 'CPF', address: 'Endereço',
}

export function DashboardPage() {
  const { user, access } = useAuth()
  const workspace = useWorkspace()
  const navigate = useNavigate()
  const [clients, setClients] = useState<ClientEntry[]>([])
  const [clientsLoading, setClientsLoading] = useState(true)
  const [clientsError, setClientsError] = useState(false)
  const [pendingItems, setPendingItems] = useState<string[]>([])

  useEffect(() => {
    api<ProfileSummary>('/profile').then((profile) => setPendingItems(profile.pendingItems ?? [])).catch(() => setPendingItems([]))
  }, [])
  useEffect(() => {
    let active = true
    setClientsLoading(true)
    setClientsError(false)
    Promise.all(workspace.companies.map(async (company) => {
      const companyClients = await api<Client[]>(`/companies/${company.id}/clients`)
      return companyClients.map((client) => ({ company, client }))
    })).then((groups) => { if (active) setClients(groups.flat().sort((a, b) => a.client.name.localeCompare(b.client.name, 'pt-BR'))) }).catch(() => { if (active) { setClients([]); setClientsError(true) } }).finally(() => { if (active) setClientsLoading(false) })
    return () => { active = false }
  }, [workspace.companies])

  const missing = useMemo(() => pendingItems.map((item) => pendingLabels[item] ?? item).join(', '), [pendingItems])
  function openClient(entry: ClientEntry) {
    workspace.selectCompany(entry.company.id)
    workspace.selectClient(entry.client.id)
    navigate('/central/transmissao')
  }

  return <section className="overview-page-reference">
    <article className="overview-welcome-card">
      <div className="overview-welcome-content">
        <span className="overview-welcome-accent" />
        <h1>Bem-vindo, {user?.fullName.trim()}</h1>
        <p>Acompanhe sua operação de segurança hoje.</p>
      </div>
    </article>

    <div className="overview-content">
      {(workspace.loading || clientsLoading) && <p role="status">Carregando seus clientes…</p>}
      {clientsError && <p role="alert">Não foi possível carregar os clientes. <button onClick={() => void workspace.reload()}>Tentar novamente</button></p>}
      {clients.length > 0 && <section className="overview-clients">
        <h2>Seus clientes</h2>
        <div>{clients.map((entry) => <button key={`${entry.company.id}-${entry.client.id}`} onClick={() => openClient(entry)}>
          <i>▥</i><span><strong>{entry.client.name}</strong><small>{entry.company.tradeName}</small></span><b>→</b>
        </button>)}</div>
      </section>}

      {(access.isGeneralAdministrator || access.hasCompanyAccess) && !access.clientOnly && <button className="overview-events-card" onClick={() => navigate(access.isGeneralAdministrator ? '/central/eventos-gerais' : '/central/eventos-empresa')}>
        <i>▣</i><span><strong>Eventos</strong><small>Consulte os vídeos detectados e filtre por empresa, cliente e grupo.</small></span><b>→</b>
      </button>}

      {pendingItems.length > 0 && <article className="overview-pending-card">
        <i>ⓘ</i><span><strong>Complete seu perfil</strong><small>{missing}</small></span><button onClick={() => navigate('/central/conta')}>Completar agora →</button>
      </article>}

      <article className="overview-android-card">
        <span><i>●</i><strong>Aplicativo Spectra Guard AI</strong></span>
        <a href={`${API_URL}/mobile/android/download`}>↓ Baixar APK</a>
      </article>
      <article className="overview-android-card">
        <span><i>▦</i><strong>Spectra Guard AI para Windows</strong></span>
        <WindowsDownloadLink />
      </article>
      <article className="overview-android-card">
        <span><i>▣</i><strong>Spectra Remote para Windows</strong></span>
        <a href="/remote/SpectraRemote-Windows-x64-Setup.exe" download>↓ Baixar Spectra Remote</a>
      </article>
      <StationDownloads />
    </div>
  </section>
}
